<script setup>
// Contrato do cliente no SGP. Com um contrato só, o servidor já grava a escolha
// sozinho; com vários, quem decide é o operador (chutar cobraria o endereço
// errado).
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import messages from './i18n';
import { erroDaApi } from './api';
import SgpSelo from './SgpSelo.vue';

const props = defineProps({
  api: { type: Object, required: true },
  contato: { type: Object, required: true },
});
const emit = defineEmits(['salvo']);

const { t } = useI18n({ messages });

const contratos = ref(null);
const carregando = ref(false);
const salvando = ref(false);
const erro = ref('');

const selecionado = computed(() =>
  contratos.value?.find(c => String(c.id) === props.contato.contrato)
);

const rotulo = c =>
  [
    t('NEUXS_SGP.CONTRATO.UNICO', { id: c.id }),
    c.cidade ? `${c.cidade}${c.uf ? `/${c.uf}` : ''}` : c.endereco,
  ]
    .filter(Boolean)
    .join(' · ');

const busca = async () => {
  carregando.value = true;
  erro.value = '';
  try {
    const { data } = await props.api.contratos();
    contratos.value = data.contratos;
    // O servidor pode ter gravado o contrato único agora.
    if (data.contato.contrato !== props.contato.contrato) {
      emit('salvo', data.contato);
    }
  } catch (error) {
    erro.value = erroDaApi(error, t('NEUXS_SGP.CONTRATO.INDISPONIVEL'));
  } finally {
    carregando.value = false;
  }
};

const escolhe = async event => {
  salvando.value = true;
  erro.value = '';
  try {
    const { data } = await props.api.salvaContrato(event.target.value);
    emit('salvo', data.contato);
  } catch (error) {
    erro.value = erroDaApi(error, t('NEUXS_SGP.CONTRATO.ERRO'));
  } finally {
    salvando.value = false;
  }
};

onMounted(busca);
</script>

<template>
  <div class="flex flex-col gap-1.5">
    <div
      v-if="carregando && !contratos"
      class="flex items-center gap-2 text-n-slate-11"
    >
      <Spinner :size="12" />
      {{ t('NEUXS_SGP.CONTRATO.BUSCANDO') }}
    </div>
    <div v-else-if="!contratos" class="flex items-center gap-2 text-n-slate-11">
      {{ erro }}
      <Button
        icon="i-lucide-refresh-cw"
        size="xs"
        variant="ghost"
        color="slate"
        :title="t('NEUXS_SGP.TENTAR_DE_NOVO')"
        @click="busca"
      />
    </div>
    <p v-else-if="contratos.length === 0" class="text-n-slate-11">
      {{ t('NEUXS_SGP.CONTRATO.NENHUM') }}
    </p>
    <span v-else-if="contratos.length === 1" class="font-medium">
      {{ rotulo(contratos[0]) }}
    </span>
    <select
      v-else
      :value="contato.contrato || ''"
      :disabled="salvando"
      class="w-full px-2 py-1 mb-0 text-sm rounded-lg outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12"
      @change="escolhe"
    >
      <option value="" disabled>{{ t('NEUXS_SGP.CONTRATO.SELECIONE') }}</option>
      <option v-for="c in contratos" :key="c.id" :value="String(c.id)">
        {{ rotulo(c) }}
      </option>
    </select>

    <!-- O status é o que o operador precisa saber antes de prometer algo. -->
    <div
      v-if="selecionado || contratos?.length === 1"
      class="flex flex-wrap items-center gap-1.5 text-xs text-n-slate-11"
    >
      <template v-for="c in [selecionado || contratos[0]]" :key="c.id">
        <SgpSelo :tom="c.tom" :texto="c.status || '—'" />
        <span v-if="c.motivo">{{ c.motivo }}</span>
        <span v-if="c.plano" class="truncate">{{ c.plano }}</span>
      </template>
    </div>
    <p v-if="erro && contratos" class="text-n-ruby-11">{{ erro }}</p>
  </div>
</template>
